/* tslint:disable no-switch-case-fall-through */
import { Action } from 'redux';
import { createSelector } from 'reselect';

import { Group } from './favorites.model';
import * as FavoritesActions from './favorites.actions';
import { AppState } from '@iv/store/app.reducer';

export interface FavoritesEntities {
  [id: string]: Group;
}

export interface FavoritesState {
  ids: string[];
  groups: FavoritesEntities;
  defaultGroups: FavoritesEntities;
  currentGroupId?: any;
}

const initialState: FavoritesState = {
  ids: [],
  groups: {},
  defaultGroups: {},
  currentGroupId: null
};

const sortGroups = function (group1, group2) {
  if (group1.name.toLowerCase() < group2.name.toLowerCase()) {
    return -1;
  }
  if (group1.name.toLowerCase() > group2.name.toLowerCase()) {
    return 1;
  }
  return 0;
};

export const FavoritesReducer =
  function (state: FavoritesState = initialState, action: Action): FavoritesState {
    switch (action.type) {
      case FavoritesActions.ADD_DEFAULT_GROUP: {
        const group = (<FavoritesActions.AddGroupAction>action).group;
        return {
          ids: [...state.ids, group.id],
          currentGroupId: state.currentGroupId,
          groups: state.groups,
          defaultGroups: Object.assign({}, state.defaultGroups, {
            [group.id]: group
          })
        };
      }

      case FavoritesActions.ADD_GROUP: {
        const group = (<FavoritesActions.AddGroupAction>action).group;
        return {
          ids: [...state.ids, group.id],
          currentGroupId: state.currentGroupId,
          groups: Object.assign({}, state.groups, {
            [group.id]: group
          }),
          defaultGroups: state.defaultGroups
        };
      }

      case FavoritesActions.SELECT_GROUP: {
        const groupId = (<FavoritesActions.SelectGroupAction>action).groupId;
        const group = Object.assign({}, state.groups, state.defaultGroups)[groupId];
        return {
          ids: state.ids,
          currentGroupId: group.id,
          groups: state.groups,
          defaultGroups: state.defaultGroups
        };
      }

      case FavoritesActions.DELETE_GROUP: {
        const groupId = (<FavoritesActions.DeleteGroupAction>action).groupId;
        const newCurrentGroupId = (<FavoritesActions.DeleteGroupAction>action).newCurrentGroupId;
        const oldEntities = state.groups;
        delete oldEntities[groupId];
        const newEntities = Object.assign({}, oldEntities);
        const newIds = Object.keys(Object.assign({}, state.defaultGroups, newEntities));
        return {
          ids: newIds,
          currentGroupId: newIds[newCurrentGroupId],
          groups: Object.assign({}, newEntities),
          defaultGroups: state.defaultGroups
        };
      }

      default:
        return state;
    }
  };

export const getFavoritesState = (state: AppState): FavoritesState => {
  return state.favorites;
};

export const getFavoritesEntities = createSelector(
  getFavoritesState,
  (state: FavoritesState) => {
    return state.groups;
  }
);

export const getFavoritesDefaultEntities = createSelector(
  getFavoritesState,
  (state: FavoritesState) => {
    return state.defaultGroups;
  }
);

export const getFavoritesAllEntities = createSelector(
  getFavoritesState,
  (state: FavoritesState) => {
    return Object.assign({}, state.groups, state.defaultGroups);
  }
);

export const getGroups = createSelector(
  getFavoritesEntities,
  (groups: FavoritesEntities) => Object.keys(groups)
    .map((groupId) => groups[groupId]).sort(sortGroups)
);


export const getDefaultGroups = createSelector(
  getFavoritesDefaultEntities,
  (groups: FavoritesEntities) => Object.keys(groups).map((groupId) => groups[groupId]).sort(sortGroups)
);

export const getCurrentGroup = createSelector(
  getFavoritesAllEntities,
  getFavoritesState,
  (groups: FavoritesEntities, state: FavoritesState) => {
    return groups[state.currentGroupId];
  }
);
